import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Package, CheckCircle2, Truck, X as XIcon, IndianRupee, User as UserIcon, MapPin, Box, ArrowRight, ShoppingBag, Clock } from "lucide-react";

const STATUS_FLOW = {
  placed:    { color: "bg-sun/40 text-ink",     next: [{ s: "confirmed", label: "Accept", icon: CheckCircle2 }, { s: "cancelled", label: "Reject", icon: XIcon }] },
  confirmed: { color: "bg-sky/15 text-sky",     next: [{ s: "packed",    label: "Mark packed", icon: Box },     { s: "cancelled", label: "Cancel", icon: XIcon }] },
  packed:    { color: "bg-brand/15 text-brand", next: [{ s: "shipped",   label: "Mark shipped", icon: Truck },  { s: "cancelled", label: "Cancel", icon: XIcon }] },
  shipped:   { color: "bg-brand/15 text-brand", next: [{ s: "delivered", label: "Mark delivered & earn", icon: CheckCircle2 }] },
  delivered: { color: "bg-leaf/40 text-ink",    next: [] },
  cancelled: { color: "bg-surface-muted text-ink-soft", next: [] },
};

const CONFIRM_COPY = {
  cancelled: { title: "Cancel this order?", body: "The customer will be notified. This cannot be undone.", cta: "Yes, cancel order", tone: "danger" },
  delivered: { title: "Mark this order as delivered?", body: "Earnings (5% platform fee deducted) will be added to your next weekly payout.", cta: "Confirm delivery & log earning", tone: "success" },
};

export default function StoreOrders() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [confirming, setConfirming] = useState(null); // { order, status }

  const load = () => axios.get(`${API}/partner/store/orders`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  const requestTransition = (o, status) => {
    if (CONFIRM_COPY[status]) setConfirming({ order: o, status });
    else transitionNow(o, status);
  };

  const transitionNow = async (o, status) => {
    setBusy(o.order_id);
    try {
      await axios.patch(`${API}/partner/store/orders/${o.order_id}`, { status });
      setConfirming(null);
      load();
    } finally { setBusy(""); }
  };

  if (!user) return null;
  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <div className="text-ink-soft">Set up your store first via <a href="/app/partner" className="text-brand font-bold">Settings</a>.</div>;

  const items = data.items || [];
  const counts = items.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-6" data-testid="store-orders-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><ShoppingBag className="w-4 h-4"/>Store · orders</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Orders</h1>
        <p className="text-ink-soft text-sm">Accept → pack → ship → deliver. Earnings (5% commission) are logged on delivery.</p>
      </header>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
        {Object.keys(STATUS_FLOW).map(s => (
          <div key={s} className="bg-surface border border-line rounded-2xl p-3 text-center">
            <div className="font-heading text-xl font-extrabold">{counts[s] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft mt-0.5">{s}</div>
          </div>
        ))}
      </div>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        {items.length === 0 ? (
          <p className="text-ink-soft text-sm">No orders yet. Add products from <a href="/app/store/products" className="text-brand font-bold">Products</a> so pet parents can buy from you.</p>
        ) : (
          <div className="divide-y divide-line">
            {items.map(o => (
              <article key={o.order_id} data-testid={`order-${o.order_id}`} className="py-4">
                <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-heading font-extrabold inline-flex items-center gap-1.5"><Package className="w-4 h-4 text-brand"/>Order <span className="font-mono text-xs text-ink-soft">{o.order_id.slice(-8)}</span></div>
                    <div className="text-xs text-ink-soft inline-flex items-center gap-1.5 mt-0.5">
                      <UserIcon className="w-3 h-3"/>{o.user_name} <span className="opacity-50">·</span> <Clock className="w-3 h-3"/>{new Date(o.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-heading font-extrabold"><IndianRupee className="w-3.5 h-3.5"/>{o.total}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_FLOW[o.status]?.color}`}>{o.status}</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {(STATUS_FLOW[o.status]?.next || []).map(({ s, label, icon: I }) => (
                      <button key={s} onClick={() => requestTransition(o, s)} disabled={busy === o.order_id} data-testid={`btn-${s}-${o.order_id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50 ${s === "cancelled" ? "bg-surface-muted text-ink-soft hover:bg-brand/10" : "bg-brand text-white hover:bg-brand-hover"}`}>
                        <I className="w-3 h-3"/>{label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)} className="text-xs font-bold text-brand hover:underline">{expanded === o.order_id ? "Hide" : "Details"}</button>
                </div>
                {expanded === o.order_id && (
                  <div className="mt-3 bg-surface-muted/40 rounded-2xl p-3 text-xs space-y-2">
                    <div className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{o.delivery_address}{o.delivery_pincode && ` · ${o.delivery_pincode}`}{o.delivery_phone && ` · ${o.delivery_phone}`}</div>
                    <div>
                      <div className="font-bold uppercase tracking-wider text-[10px] mb-1 text-ink-soft">Items</div>
                      {o.items.map((it, i) => (
                        <div key={i} className="flex justify-between py-0.5">
                          <span>{it.name} <span className="text-ink-soft">× {it.quantity}</span></span>
                          <span className="font-bold">₹{it.price * it.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {confirming && (
        <ConfirmDialog
          {...CONFIRM_COPY[confirming.status]}
          orderId={confirming.order.order_id}
          busy={busy === confirming.order.order_id}
          onConfirm={() => transitionNow(confirming.order, confirming.status)}
          onClose={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

function ConfirmDialog({ title, body, cta, tone = "neutral", orderId, busy, onConfirm, onClose }) {
  const ctaCls = tone === "danger"
    ? "bg-brand text-white hover:bg-brand-hover"
    : tone === "success"
      ? "bg-brand-green text-white hover:opacity-90"
      : "bg-ink text-white hover:bg-brand";
  const iconCls = tone === "danger" ? "bg-brand/10 text-brand" : tone === "success" ? "bg-leaf/40 text-brand-green" : "bg-surface-muted text-ink";
  const Icon = tone === "danger" ? XIcon : CheckCircle2;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 animate-fade-in" onClick={onClose} data-testid="confirm-dialog">
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-sm shadow-soft">
        <div className={`w-12 h-12 rounded-2xl grid place-items-center ${iconCls} mb-3`}><Icon className="w-6 h-6"/></div>
        <h3 className="font-heading text-xl font-extrabold leading-tight">{title}</h3>
        <p className="text-sm text-ink-soft mt-2">{body}</p>
        <div className="text-[10px] font-mono text-ink-soft/70 mt-2">Order {orderId.slice(-10)}</div>
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} disabled={busy} className="flex-1 rounded-full py-2.5 text-sm font-bold bg-surface-muted text-ink hover:bg-line" data-testid="confirm-cancel">Keep as-is</button>
          <button onClick={onConfirm} disabled={busy} data-testid="confirm-go" className={`flex-1 rounded-full py-2.5 text-sm font-bold disabled:opacity-50 ${ctaCls}`}>{busy ? "Saving…" : cta}</button>
        </div>
      </div>
      <style>{`.animate-fade-in{animation:fi .15s ease-out}@keyframes fi{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}
